<script>
	export let name;

	const features = [
		{
			title: 'Real-Time Dashboard',
			desc: 'At-a-glance metrics with live data streaming from every asset on your microgrid.',
			img: 'assets/img/ss-home.png'
		},
		{
			title: 'Interactive Site Map',
			desc: 'Mapped site assets with detailed information per unit. Visualize your entire energy infrastructure.',
			img: 'assets/img/ss-map.png'
		},
		{
			title: 'Inverter Diagnostics',
			desc: 'Live telemetry and automated diagnostics from inverters. Instant fault detection and performance analysis.',
			img: 'assets/img/ss-inv.png'
		}
	];

	const capabilities = [
		{ icon: '⚡', title: 'Predictive Analytics', desc: 'AI-driven forecasting for energy production, consumption patterns, and maintenance scheduling.' },
		{ icon: '🔌', title: 'Hardware Agnostic', desc: 'Integrates with any inverter, battery, meter, or controller regardless of manufacturer.' },
		{ icon: '📊', title: 'Automated Reporting', desc: 'Generate compliance reports, performance summaries, and billing data automatically.' },
		{ icon: '🔒', title: 'Secure & Reliable', desc: 'Enterprise-grade security with redundant data handling and 99.9% uptime.' },
		{ icon: '🎛️', title: 'Load Management', desc: 'Intelligent load control and switching to optimize power distribution across your grid.' },
		{ icon: '☁️', title: 'Cloud Platform', desc: 'Access your microgrid data from anywhere. No on-site hardware required for monitoring.' }
	];

	let activeFeature = 0;
</script>

<main>
	<!-- HERO -->
	<section class="hero">
		<div class="hero-grid"></div>
		<div class="hero-content">
			<div class="hero-brand">
				<img src="assets/img/logo.png" alt="GridBrain Logo" class="logo" />
				<h1>GridBrain</h1>
			</div>
			<p class="tagline">{name}</p>
			<p class="hero-desc">A comprehensive, hardware-agnostic digital platform to monitor, control, and optimize complex microgrid systems with multiple power sources and metering devices.</p>
			<div class="hero-actions">
				<a href="#features" class="btn btn-primary">Explore Features</a>
				<a href="mailto:hello@x10engineers.com" class="btn btn-outline">Request Demo</a>
			</div>
		</div>
		<div class="hero-glow"></div>
	</section>

	<!-- FEATURES -->
	<section class="features" id="features">
		<div class="section-header">
			<span class="label">Platform</span>
			<h2>Intelligent Microgrid Control</h2>
			<p>Monitor every asset, diagnose issues in real time, and make data-driven decisions from a single unified dashboard.</p>
		</div>

		<div class="feature-tabs">
			{#each features as feature, i}
				<button
					class="feature-tab"
					class:active={activeFeature === i}
					on:click={() => activeFeature = i}
				>
					<span class="tab-num">0{i + 1}</span>
					{feature.title}
				</button>
			{/each}
		</div>

		<div class="feature-display">
			<div class="feature-screenshot">
				<img src={features[activeFeature].img} alt={features[activeFeature].title} loading="lazy" />
			</div>
			<div class="feature-info">
				<h3>{features[activeFeature].title}</h3>
				<p>{features[activeFeature].desc}</p>
			</div>
		</div>
	</section>

	<!-- CAPABILITIES -->
	<section class="capabilities">
		<div class="section-header">
			<span class="label">Capabilities</span>
			<h2>Built for Complex Energy Systems</h2>
			<p>Everything you need to manage multi-source microgrids at scale.</p>
		</div>
		<div class="cap-grid">
			{#each capabilities as cap}
				<div class="cap-card">
					<span class="cap-icon">{cap.icon}</span>
					<h4>{cap.title}</h4>
					<p>{cap.desc}</p>
				</div>
			{/each}
		</div>
	</section>

	<!-- CTA -->
	<section class="cta">
		<div class="cta-card">
			<h2>Ready to optimize your microgrid?</h2>
			<p>Get in touch with our team to schedule a demo and see how GridBrain can transform your energy infrastructure.</p>
			<div class="cta-actions">
				<a href="mailto:hello@x10engineers.com" class="btn btn-primary">Contact Us</a>
				<a href="https://x10engineers.com" class="btn btn-outline" target="_blank">Visit X10 Engineers</a>
			</div>
		</div>
	</section>

	<!-- FOOTER -->
	<footer>
		<div class="footer-content">
			<div class="footer-brand">
				<img src="assets/img/logo.png" alt="GridBrain" class="footer-logo" />
				<span>GridBrain</span>
			</div>
			<p class="footer-copy">&copy; {new Date().getFullYear()} X10 Engineers, LLC. All rights reserved.</p>
		</div>
	</footer>
</main>

<style>
	/* ── BASE ── */
	:root {
		--bg: #060a14;
		--bg-card: rgba(15, 20, 35, 0.85);
		--accent: #ff8c00;
		--accent-dim: rgba(255, 140, 0, 0.12);
		--accent-glow: rgba(255, 140, 0, 0.25);
		--accent-gradient: linear-gradient(135deg, #ff8c00, #f97316);
		--cyan: #00d4ff;
		--cyan-dim: rgba(0, 212, 255, 0.1);
		--text: #e8ecf4;
		--text-secondary: #8a95a8;
		--text-muted: #5a6577;
		--border: rgba(255, 140, 0, 0.1);
		--border-subtle: rgba(255, 255, 255, 0.06);
		--radius: 12px;
		--transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	main {
		background: var(--bg);
		color: var(--text);
		font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
		line-height: 1.6;
		max-width: 100%;
		margin: 0;
		padding: 0;
		overflow-x: hidden;
	}

	/* ── HERO ── */
	.hero {
		position: relative;
		min-height: 100vh;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 120px 24px 80px;
		overflow: hidden;
	}

	.hero-grid {
		position: absolute;
		inset: 0;
		background-image:
			linear-gradient(var(--accent-dim) 1px, transparent 1px),
			linear-gradient(90deg, var(--accent-dim) 1px, transparent 1px);
		background-size: 80px 80px;
		opacity: 0.2;
	}

	.hero-glow {
		position: absolute;
		width: 600px;
		height: 600px;
		background: radial-gradient(circle, var(--accent-glow), transparent 70%);
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		pointer-events: none;
		opacity: 0.3;
	}

	.hero-content {
		position: relative;
		z-index: 2;
		max-width: 720px;
	}

	.hero-brand {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 16px;
		margin-bottom: 16px;
	}

	.logo {
		width: 72px;
		height: auto;
		filter: drop-shadow(0 0 20px var(--accent-glow));
	}

	h1 {
		font-size: clamp(3rem, 6vw, 4.5rem);
		font-weight: 800;
		color: var(--accent);
		letter-spacing: -0.02em;
		line-height: 1;
	}

	.tagline {
		font-size: 1.3rem;
		color: var(--text-secondary);
		font-weight: 300;
		letter-spacing: 0.15em;
		text-transform: uppercase;
		margin-bottom: 24px;
	}

	.hero-desc {
		font-size: 1.1rem;
		color: var(--text-secondary);
		max-width: 580px;
		margin: 0 auto 40px;
		line-height: 1.7;
	}

	.hero-actions {
		display: flex;
		gap: 16px;
		justify-content: center;
		flex-wrap: wrap;
	}

	/* ── BUTTONS ── */
	.btn {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 12px 28px;
		border-radius: 8px;
		font-weight: 600;
		font-size: 0.95rem;
		font-family: inherit;
		border: none;
		cursor: pointer;
		transition: all var(--transition);
		text-decoration: none;
	}

	.btn-primary {
		background: var(--accent-gradient);
		color: #0a0e1a;
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		box-shadow: 0 8px 30px var(--accent-glow);
	}

	.btn-outline {
		background: transparent;
		border: 1px solid var(--border);
		color: var(--text);
	}

	.btn-outline:hover {
		border-color: var(--accent);
		background: var(--accent-dim);
	}

	/* ── SECTIONS ── */
	section {
		padding: 100px 24px;
		max-width: 1100px;
		margin: 0 auto;
	}

	.section-header {
		text-align: center;
		margin-bottom: 64px;
	}

	.label {
		display: inline-block;
		padding: 4px 14px;
		border-radius: 100px;
		background: var(--accent-dim);
		border: 1px solid var(--border);
		color: var(--accent);
		font-size: 0.75rem;
		font-weight: 600;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		margin-bottom: 16px;
		font-family: 'JetBrains Mono', monospace;
	}

	.section-header h2 {
		font-size: clamp(1.8rem, 4vw, 2.5rem);
		font-weight: 700;
		margin-bottom: 12px;
		letter-spacing: -0.02em;
	}

	.section-header p {
		font-size: 1.05rem;
		color: var(--text-secondary);
		max-width: 560px;
		margin: 0 auto;
	}

	/* ── FEATURES ── */
	.features {
		border-top: 1px solid var(--border-subtle);
	}

	.feature-tabs {
		display: flex;
		gap: 8px;
		margin-bottom: 32px;
		justify-content: center;
		flex-wrap: wrap;
	}

	.feature-tab {
		padding: 10px 20px;
		border-radius: 8px;
		background: var(--bg-card);
		border: 1px solid var(--border-subtle);
		color: var(--text-secondary);
		font-family: inherit;
		font-size: 0.9rem;
		font-weight: 500;
		cursor: pointer;
		transition: all var(--transition);
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.feature-tab:hover {
		border-color: var(--border);
		color: var(--text);
	}

	.feature-tab.active {
		background: var(--accent-dim);
		border-color: var(--accent);
		color: var(--accent);
	}

	.tab-num {
		font-family: 'JetBrains Mono', monospace;
		font-size: 0.75rem;
		opacity: 0.5;
	}

	.feature-display {
		background: var(--bg-card);
		border: 1px solid var(--border-subtle);
		border-radius: 16px;
		overflow: hidden;
	}

	.feature-screenshot {
		padding: 24px 24px 0;
		overflow: hidden;
	}

	.feature-screenshot img {
		width: 100%;
		border-radius: 10px 10px 0 0;
		display: block;
		box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.3);
	}

	.feature-info {
		padding: 32px;
		border-top: 1px solid var(--border-subtle);
	}

	.feature-info h3 {
		font-size: 1.2rem;
		font-weight: 600;
		margin-bottom: 8px;
		color: var(--accent);
	}

	.feature-info p {
		color: var(--text-secondary);
		font-size: 0.95rem;
	}

	/* ── CAPABILITIES ── */
	.capabilities {
		border-top: 1px solid var(--border-subtle);
	}

	.cap-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		gap: 20px;
	}

	.cap-card {
		padding: 28px;
		background: var(--bg-card);
		border: 1px solid var(--border-subtle);
		border-radius: var(--radius);
		transition: all var(--transition);
	}

	.cap-card:hover {
		border-color: var(--border);
		transform: translateY(-2px);
	}

	.cap-icon {
		font-size: 1.5rem;
		display: block;
		margin-bottom: 12px;
	}

	.cap-card h4 {
		font-size: 1rem;
		font-weight: 600;
		margin-bottom: 6px;
	}

	.cap-card p {
		font-size: 0.85rem;
		color: var(--text-secondary);
		line-height: 1.5;
	}

	/* ── CTA ── */
	.cta {
		border-top: 1px solid var(--border-subtle);
	}

	.cta-card {
		text-align: center;
		padding: 64px 40px;
		background: linear-gradient(135deg, var(--accent-dim), var(--cyan-dim));
		border: 1px solid var(--border);
		border-radius: 20px;
	}

	.cta-card h2 {
		font-size: clamp(1.5rem, 3vw, 2rem);
		font-weight: 700;
		margin-bottom: 12px;
	}

	.cta-card p {
		color: var(--text-secondary);
		max-width: 520px;
		margin: 0 auto 32px;
		font-size: 1rem;
	}

	.cta-actions {
		display: flex;
		gap: 16px;
		justify-content: center;
		flex-wrap: wrap;
	}

	/* ── FOOTER ── */
	footer {
		padding: 32px 24px;
		border-top: 1px solid var(--border-subtle);
		max-width: 1100px;
		margin: 0 auto;
	}

	.footer-content {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.footer-brand {
		display: flex;
		align-items: center;
		gap: 10px;
		font-weight: 600;
		font-size: 0.95rem;
	}

	.footer-logo {
		width: 28px;
		opacity: 0.7;
	}

	.footer-copy {
		font-size: 0.8rem;
		color: var(--text-muted);
	}

	/* ── RESPONSIVE ── */
	@media (max-width: 768px) {
		.hero { min-height: auto; padding: 100px 24px 60px; }
		h1 { font-size: 2.5rem; }
		.tagline { font-size: 1rem; }
		.cap-grid { grid-template-columns: 1fr; }
		.footer-content { flex-direction: column; gap: 12px; text-align: center; }
		.hero-actions { flex-direction: column; align-items: center; }
		.btn { width: 100%; max-width: 280px; justify-content: center; }
		.cta-actions { flex-direction: column; align-items: center; }
	}
</style>
